import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useLocale } from "./locale";
import { useFirstInteraction, useIdle } from "./defer";

const API = process.env.REACT_APP_BACKEND_URL;
export const GATE_KEY = "vl_radio_gate";
export const OFF_KEY = "vl_radio_off";
export const POPUP_KEY = "vl_radio_popup"; // set once the first-visit player was collapsed or closed → never shown expanded again
const STATE_KEY = "vl_radio_state";
const saved = () => { try { return JSON.parse(sessionStorage.getItem(STATE_KEY) || "null"); } catch { return null; } };
const GATE_AFTER_STORY = 2;
const QUIET = ["/ops", "/marketing", "/embed", "/portal", "/login", "/signup", "/reset", "/admin"];
const Ctx = createContext(null);
export const isFirstVisit = () => { try { return !localStorage.getItem(POPUP_KEY) && !localStorage.getItem(OFF_KEY); } catch { return false; } };

export function RadioProvider({ children }) {
  const { iso } = useLocale();
  const { pathname } = useLocation();
  const audio = useRef(null);
  const [bulletin, setBulletin] = useState(null);
  const [i, setI] = useState(() => saved()?.i || 0);
  const [playing, setPlaying] = useState(false);
  const [started, setStarted] = useState(() => !!saved()?.started);
  const [blocked, setBlocked] = useState(false);
  const [gate, setGate] = useState(false);
  const [pct, setPct] = useState(0);
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(OFF_KEY) === "1");
  const [popup, setPopup] = useState(isFirstVisit);
  const autoTried = useRef(false);
  const segs = useMemo(() => bulletin?.segments || [], [bulletin]);
  const seg = segs[i];
  const autoPage = !QUIET.some((p) => pathname === p || pathname.startsWith(`${p}/`));
  const popupPage = autoPage && !pathname.startsWith("/radio"); // the radio page has its own player
  const idle = useFirstInteraction(); // bulletin loads after the first interaction, never on its own…
  const painted = useIdle(); // …except for a first-time visitor, whose expanded player appears once the page is idle

  useEffect(() => {
    const m = pathname.match(/^\/radio\/([^/]+)/);
    // The bulletin is below the fold: wait for the first paint unless it is the radio page itself, a bulletin is being resumed or the first-visit player is due.
    if (!m && !pathname.startsWith("/radio") && !started && !idle && !(popup && painted && popupPage)) return;
    const url = m ? `${API}/api/radio/bulletin/${m[1]}` : `${API}/api/radio/bulletin?region=${encodeURIComponent(iso || "")}`;
    if (!m && bulletin?.ready && bulletin.regionLoaded === iso) return;
    fetch(url).then((r) => (r.ok ? r.json() : { ready: false, missing: true, segments: [] })).then((d) => setBulletin({ ...d, regionLoaded: iso })).catch(() => setBulletin({ ready: false, segments: [] }));
  }, [iso, pathname, idle, popup && painted]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const el = audio.current; if (!el || !seg?.audio_url || !(started || playing)) return;
    el.src = seg.audio_url; setPct(0);
    if (playing) el.play().catch(() => { setPlaying(false); setBlocked(true); });
  }, [seg?.audio_url, started]); // eslint-disable-line react-hooks/exhaustive-deps

  const play = useCallback(() => {
    const el = audio.current; if (!el || !seg) return;
    setStarted(true); setDismissed(false); localStorage.removeItem(OFF_KEY);
    if (seg.audio_url && !el.src) el.src = seg.audio_url;
    el.play().then(() => { setPlaying(true); setBlocked(false); }).catch(() => { setPlaying(false); setBlocked(true); });
  }, [seg]);
  const pause = useCallback(() => { audio.current?.pause(); setPlaying(false); }, []);
  const stop = useCallback(() => { audio.current?.pause(); setPlaying(false); setStarted(false); setBlocked(false); setDismissed(true); setPopup(false); localStorage.setItem(OFF_KEY, "1"); localStorage.setItem(POPUP_KEY, "off"); sessionStorage.removeItem(STATE_KEY); }, []);
  // First-visit player → small bubble in the corner. Remembered across visits, so the expanded player never comes back.
  const collapse = useCallback((reason = "collapsed") => { audio.current?.pause(); setPlaying(false); setStarted(false); setPopup(false); localStorage.setItem(POPUP_KEY, reason); sessionStorage.removeItem(STATE_KEY); }, []);
  const jump = useCallback((n) => setI(Math.max(0, Math.min(segs.length - 1, n))), [segs.length]);

  // First visit only: the expanded player tries to start the bulletin by itself; when the browser needs a tap first, the play button pulses instead.
  useEffect(() => {
    if (!popup || !popupPage || !bulletin?.ready || autoTried.current || dismissed) return;
    const el = audio.current; const first = bulletin.segments?.[0]; if (!el || !first?.audio_url) return;
    autoTried.current = true;
    setStarted(true); setI(0); el.src = first.audio_url;
    el.play().then(() => { setPlaying(true); setBlocked(false); }).catch(() => { setPlaying(false); setBlocked(true); });
  }, [popup, popupPage, bulletin?.ready]); // eslint-disable-line react-hooks/exhaustive-deps

  // Any press outside the expanded player stops the audio and collapses it to the corner.
  useEffect(() => {
    if (!popup || !popupPage) return;
    const h = (e) => { if (e.target.closest?.("[data-radio-popup]")) return; collapse("collapsed"); };
    document.addEventListener("pointerdown", h, true);
    return () => document.removeEventListener("pointerdown", h, true);
  }, [popup, popupPage, collapse]);

  useEffect(() => { if (bulletin?.ready) sessionStorage.setItem(STATE_KEY, JSON.stringify({ started, i, id: bulletin.base_id || bulletin.id })); }, [started, i, bulletin?.ready, bulletin?.id, bulletin?.base_id]);
  useEffect(() => {
    // No autoplay on later visits: the radio only resumes a bulletin the visitor started themselves earlier in this session.
    if (!autoPage) { audio.current?.pause(); return; }
    if (!bulletin?.ready || dismissed || !started || popup) return;
    const prev = saved();
    const resume = prev?.id === (bulletin.base_id || bulletin.id) && prev.i < segs.length && prev.started;
    if (!resume) { setStarted(false); setI(0); return; }
    if (playing) return;
    const t = setTimeout(() => audio.current?.play().then(() => { setPlaying(true); setBlocked(false); }).catch(() => { setPlaying(false); setBlocked(true); }), 600);
    return () => clearTimeout(t);
  }, [bulletin?.ready, autoPage]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const go = () => { play(); document.getElementById("newsroom")?.scrollIntoView({ behavior: "smooth", block: "start" }); };
    window.addEventListener("vl-radio-play", go);
    return () => window.removeEventListener("vl-radio-play", go);
  }, [play]);

  const ended = () => {
    // Keep rolling through every segment; the subscribe prompt appears alongside the audio instead of interrupting it.
    if (seg?.kind === "story" && seg.n === GATE_AFTER_STORY && !localStorage.getItem(GATE_KEY)) setGate(true);
    if (i < segs.length - 1) { setI(i + 1); setPlaying(true); } else { setPlaying(false); setStarted(false); }
  };
  const closeGate = () => setGate(false);

  const value = useMemo(() => ({ bulletin, segs, seg, i, playing, started, blocked, gate, pct, dismissed, popup: popup && popupPage, quiet: !autoPage, play, pause, stop, jump, collapse, closeGate, toggle: () => (playing ? pause() : play()) }), [bulletin, segs, seg, i, playing, started, blocked, gate, pct, dismissed, popup, autoPage, popupPage, play, pause, stop, jump, collapse]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <Ctx.Provider value={value}>
      {children}
      <audio ref={audio} preload="none" onEnded={ended} onTimeUpdate={(e) => setPct(e.target.duration ? (e.target.currentTime / e.target.duration) * 100 : 0)} onPause={() => setPlaying(false)} onPlay={() => setPlaying(true)} data-testid="radio-audio" />
    </Ctx.Provider>
  );
}

export const useRadio = () => useContext(Ctx);
